/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Briefcase, Eye, MessageSquareWarning, Newspaper, RefreshCw } from 'lucide-react';
import { fetchPortfolio, fetchWatchlist, fetchStockRumors, fetchLatestNews } from '../store/slices/portfolioSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { NewsItem } from '../components/news/NewsItem';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { DebateLauncher } from '../components/debate/DebateLauncher';
import { AMFCard } from '../components/stock/AMFCard';
import { MovesCard } from '../components/reports/MovesCard';
import { TrialLauncher } from '../components/stock/TrialLauncher';
import { formatNumber } from '../theme/format';
import {
  Badge, Banner, Card, CardHeader, IconButton, Money, Percent, SkeletonList, SkeletonText, Stat, SymbolMark,
} from '../components/ui';

const StockDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { positions, watchlist, saxoConnected, stockRumors, stockRumorsLoading, latestNews, latestNewsLoading } = useSelector((state) => state.portfolio);
  const { language } = useLanguage();
  const position = useMemo(() => positions.find(p => p.symbol === symbol), [positions, symbol]);
  const watched = useMemo(() => watchlist.find(item => item.symbol === symbol), [watchlist, symbol]);
  const stock = position || watched;
  const cacheKey = `${symbol}_${language}`;
  const rumors = stockRumors[cacheKey];
  const isRumorsLoading = !!stockRumorsLoading[symbol];

  useEffect(() => {
    if (saxoConnected && positions.length === 0) {
      dispatch(fetchPortfolio());
    }
  }, [dispatch, saxoConnected, positions.length]);

  useEffect(() => { dispatch(fetchWatchlist()); }, [dispatch]);

  useEffect(() => {
    if (saxoConnected && stock && !rumors && !isRumorsLoading) {
      dispatch(fetchStockRumors({ symbol, description: stock.description, language }));
    }
  }, [dispatch, saxoConnected, stock, symbol, rumors, isRumorsLoading, language]);

  const stockNews = useMemo(() => {
    if (!latestNews?.items) return [];
    return latestNews.items.filter(item => item.symbol === symbol);
  }, [latestNews, symbol]);

  useEffect(() => {
    if (saxoConnected && stock && !latestNews && !latestNewsLoading) {
      dispatch(fetchLatestNews({ stocks: [{ symbol, description: stock.description }], language }));
    }
  }, [dispatch, saxoConnected, stock, symbol, latestNews, latestNewsLoading, language]);

  const handleRefreshRumors = () => {
    if (stock) {
      dispatch(fetchStockRumors({ symbol, description: stock.description, language, forceRefresh: true }));
    }
  };

  const currency = position?.currency || 'EUR';

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <IconButton icon={ArrowLeft} label="Retour" variant="secondary" onClick={() => navigate(-1)} />
        <SymbolMark symbol={symbol} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="break-words font-display text-display-md font-normal text-fg sm:text-display-lg">{symbol}</h1>
          <p className="mt-1 text-[15px] text-fg-muted">{stock?.description || 'Valeur hors portefeuille'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {position && <Badge tone="gold" icon={Briefcase}>En portefeuille</Badge>}
          {watched && <Badge tone="info" icon={Eye}>Watchlist</Badge>}
          <DebateLauncher symbol={symbol} description={stock?.description || ''} size="sm" />
          {position && position.asset_type !== 'Etf' && <TrialLauncher symbol={position.symbol} />}
        </div>
      </header>

      {!stock && (positions.length > 0 || !saxoConnected) && (
        <Banner tone="neutral">Cette valeur n’est ni en portefeuille ni dans votre watchlist : actualités et rumeurs ne sont pas chargées.</Banner>
      )}

      {position && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <Stat size="compact" label="Valorisation" value={<Money value={position.market_value} decimals={0} />} />
          <Stat size="compact" label="Plus/moins-value" value={<Money value={position.profit_loss} decimals={0} sign colorize />} />
          <Stat size="compact" label="Plus/moins-value %" value={<Percent value={position.profit_loss_pct} decimals={2} colorize />} />
          <Stat
            size="compact"
            label="Quantité"
            value={<span className="num">{formatNumber(position.amount, { decimals: Number.isInteger(Number(position.amount)) ? 0 : 2 })}</span>}
            hint={<>PRU <Money value={position.open_price} sensitive={false} currency={currency} /> → cours <Money value={position.current_price} sensitive={false} currency={currency} /></>}
          />
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-5 xl:items-start">
        <Card className="xl:col-span-2">
          <CardHeader icon={Newspaper} title="Actualités" description="Titres récents concernant cette valeur" />
          {latestNewsLoading ? (
            <div role="status">
              <span className="sr-only">Recherche d’actualités…</span>
              <SkeletonList items={3} />
            </div>
          ) : stockNews.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">Aucune actualité récente pour cette valeur.</p>
          ) : (
            <div className="divide-y divide-line">
              {stockNews.map((item, i) => <NewsItem key={`${item.url || item.headline}-${i}`} item={item} showSymbol={false} />)}
            </div>
          )}
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader
            icon={MessageSquareWarning}
            title="Rumeurs"
            description="Signaux faibles et bruits de marché, à recouper avant toute décision"
            actions={(
              <IconButton
                icon={RefreshCw}
                label={`Actualiser les rumeurs de ${symbol}`}
                variant="secondary"
                onClick={handleRefreshRumors}
                disabled={!stock || isRumorsLoading}
                iconClassName={isRumorsLoading ? 'animate-spin' : undefined}
              />
            )}
          />
          {isRumorsLoading ? (
            <div role="status">
              <span className="sr-only">Recherche des rumeurs pour {symbol}…</span>
              <SkeletonText lines={6} />
            </div>
          ) : rumors ? (
            <div className="max-h-[600px] overflow-y-auto pr-1">
              <MarkdownProse variant="rumor">{rumors.content}</MarkdownProse>
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-fg-muted">Aucune rumeur chargée pour le moment.</p>
          )}
        </Card>
      </div>

      <MovesCard symbol={symbol} />
      {stock?.description && <AMFCard symbol={symbol} name={stock.description} />}
    </div>
  );
};

export default StockDetail;
